/**
 * InstagramCredentialsCard - Card para exibir credenciais e configuração do Instagram Direct
 * 
 * Exibe as informações de configuração da inbox do Instagram incluindo:
 * - URL do Webhook para configurar no Meta Developer Portal
 * - Token de verificação para validar o webhook
 * - Status das credenciais configuradas
 * - Instruções para configurar webhook
 * 
 * @release v1.0 - 2026-01-17
 */

import { useState } from 'react';
import { 
  Instagram, 
  Copy, 
  Check, 
  Key, 
  Globe, 
  Link2,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  KeyRound
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ============= TIPOS =============

/**
 * Credenciais seguras do Instagram (sem access_token)
 * Inclui page_id obrigatório para validação de ownership do thread pela API Meta
 */
export interface InstagramCredentialsSafe {
  instagram_business_account_id: string | null;
  page_id: string | null; // ID da Página Facebook - obrigatório para envio de mensagens
  has_access_token: boolean;
  has_app_secret: boolean;
  webhook_verify_token: string | null;
  last_validated_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Resultado de validação de conexão
 */
interface ConnectionValidationResult {
  success: boolean;
  status: 'connected' | 'disconnected' | 'error' | 'no_credentials';
  message: string;
  details?: Record<string, unknown>;
}

interface InstagramCredentialsCardProps {
  /** Credenciais da inbox (pode ser null se não configuradas) */
  credentials: InstagramCredentialsSafe | null;
  /** ID da inbox para construir a URL do webhook */
  inboxId: string;
  /** Se está carregando credenciais */
  isLoading?: boolean;
  /** Callback para verificar conexão */
  onCheckConnection?: () => Promise<ConnectionValidationResult | void>;
  /** Se está verificando conexão */
  isCheckingConnection?: boolean;
  /** Resultado da última verificação */
  connectionStatus?: 'connected' | 'disconnected' | 'error' | 'pending' | null;
  /** Callback para abrir dialog de edição de credenciais */
  onEditCredentials?: () => void;
  /** Se deve exibir o botão de editar credenciais */
  showEditButton?: boolean;
}

// ============= CONSTANTES =============

import { getInstagramWebhookUrl } from '@/lib/supabaseHelpers';

// Eventos que devem ser assinados no webhook - Organizados por categoria
const WEBHOOK_EVENTS = {
  required: [
    { name: 'messages', description: 'Mensagens de texto, mídia e stories' },
    { name: 'messaging_postbacks', description: 'Cliques em botões e quick replies' },
  ],
  recommended: [
    { name: 'messaging_reactions', description: 'Reações com emoji nas mensagens' },
    { name: 'message_reactions', description: 'Reações (Graph API alternativo)' },
    { name: 'messaging_referral', description: 'Referrals de links e anúncios' },
    { name: 'messaging_seen', description: 'Confirmação de leitura' },
  ],
  optional: [
    { name: 'comments', description: 'Comentários em posts/reels' },
    { name: 'live_comments', description: 'Comentários em lives' },
    { name: 'message_edit', description: 'Mensagens editadas' },
    { name: 'messaging_handover', description: 'Handover Protocol (apps múltiplos)' },
    { name: 'messaging_optins', description: 'Opt-ins de usuários' },
  ]
};

// ============= COMPONENTE =============

export function InstagramCredentialsCard({ 
  credentials, 
  inboxId,
  isLoading = false,
  onCheckConnection,
  isCheckingConnection = false,
  connectionStatus = null,
  onEditCredentials,
  showEditButton = false
}: InstagramCredentialsCardProps) {
  // Estado para controlar cópia
  const [copiedField, setCopiedField] = useState<string | null>(null);
  
  // Obtém URL do webhook dinamicamente
  const webhookUrl = getInstagramWebhookUrl();

  /**
   * Copia texto para a área de transferência
   */
  const handleCopy = async (field: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      toast.success('Copiado para a área de transferência!');
      
      // Reset do ícone após 2 segundos
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('Erro ao copiar');
    }
  };

  /**
   * Renderiza botão de copiar com feedback visual
   */
  const CopyButton = ({ field, value }: { field: string; value: string }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => handleCopy(field, value)}
      className="h-8 px-2 shrink-0"
    >
      {copiedField === field ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </Button>
  );

  /**
   * Renderiza indicador de status
   */
  const StatusIndicator = ({ configured }: { configured: boolean }) => (
    <div className={cn(
      "flex items-center gap-1 text-xs",
      configured ? "text-green-600" : "text-amber-600"
    )}>
      {configured ? (
        <>
          <CheckCircle2 className="h-3.5 w-3.5" />
          <span>Configurado</span>
        </>
      ) : (
        <>
          <XCircle className="h-3.5 w-3.5" />
          <span>Pendente</span>
        </>
      )}
    </div>
  );

  // Verificar status geral das credenciais (inclui page_id obrigatório)
  const hasFullCredentials = credentials?.has_access_token && 
    credentials?.has_app_secret && 
    credentials?.instagram_business_account_id &&
    credentials?.page_id;

  const verifyToken = credentials?.webhook_verify_token || '';

  // ============= RENDERIZAÇÃO =============

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
              <Instagram className="h-4 w-4 text-white" />
            </div>
            Configuração do Instagram
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-10 bg-muted rounded" />
            <div className="h-10 bg-muted rounded" />
            <div className="h-10 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-base">
            <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
              <Instagram className="h-4 w-4 text-white" />
            </div>
            Configuração do Instagram
          </div>
          
          {/* Badge de status geral */}
          <Badge 
            variant={hasFullCredentials ? 'default' : 'secondary'}
            className={cn(
              hasFullCredentials 
                ? 'bg-green-500/10 text-green-600 border-green-500/20' 
                : 'bg-amber-500/10 text-amber-600 border-amber-500/20'
            )}
          >
            {hasFullCredentials ? 'Configurado' : 'Pendente'}
          </Badge>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Alerta se credenciais não estão completas - com botão de editar */}
        {!hasFullCredentials && (
          <Alert variant="default" className="border-amber-500/50 bg-amber-500/10">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <AlertDescription className="text-amber-700 dark:text-amber-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span>Configure todas as credenciais e o webhook no Meta Developer Portal para ativar a integração.</span>
              {showEditButton && onEditCredentials && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onEditCredentials}
                  className="gap-2 shrink-0"
                >
                  <KeyRound className="h-4 w-4" />
                  Preencher Credenciais
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Seção: URL do Webhook */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">URL do Webhook (Callback URL)</span>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50 border">
            <code className="flex-1 text-xs break-all font-mono">
              {webhookUrl}
            </code>
            <CopyButton field="webhookUrl" value={webhookUrl} />
          </div>
          <p className="text-xs text-muted-foreground">
            Cole esta URL no campo "Callback URL" nas configurações de Webhook do seu App no Meta.
          </p>
        </div>

        {/* Seção: Token de Verificação */}
        {verifyToken && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Key className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm">Token de Verificação</span>
              </div>
              <StatusIndicator configured={!!verifyToken} />
            </div>
            <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50 border">
              <code className="flex-1 text-xs break-all font-mono">
                {verifyToken}
              </code>
              <CopyButton field="verifyToken" value={verifyToken} />
            </div>
            <p className="text-xs text-muted-foreground">
              Cole este token no campo "Verify Token" nas configurações de Webhook.
            </p>
          </div>
        )}

        {/* Seção: Eventos para Assinar - Expandida com categorias */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-primary" />
            <span className="font-medium text-sm">Eventos para Assinar</span>
          </div>
          
          {/* Obrigatórios */}
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-red-600 dark:text-red-400">
              Obrigatórios:
            </span>
            <div className="flex flex-wrap gap-1.5">
              {WEBHOOK_EVENTS.required.map((event) => (
                <Badge 
                  key={event.name} 
                  variant="destructive" 
                  className="font-mono text-xs"
                  title={event.description}
                >
                  {event.name}
                </Badge>
              ))}
            </div>
          </div>
          
          {/* Recomendados */}
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
              Recomendados:
            </span>
            <div className="flex flex-wrap gap-1.5">
              {WEBHOOK_EVENTS.recommended.map((event) => (
                <Badge 
                  key={event.name} 
                  variant="secondary" 
                  className="font-mono text-xs"
                  title={event.description}
                >
                  {event.name}
                </Badge>
              ))}
            </div>
          </div>
          
          {/* Opcionais */}
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Opcionais:
            </span>
            <div className="flex flex-wrap gap-1.5">
              {WEBHOOK_EVENTS.optional.map((event) => (
                <Badge 
                  key={event.name} 
                  variant="outline" 
                  className="font-mono text-xs opacity-80"
                  title={event.description}
                >
                  {event.name}
                </Badge>
              ))}
            </div>
          </div>
          
          <p className="text-xs text-muted-foreground">
            Marque estes eventos no Webhook do Instagram. Passe o mouse sobre cada badge para ver a descrição.
          </p>
        </div>

        <Separator />

        {/* Seção: Status das Credenciais */}
        <div className="space-y-3">
          <span className="font-medium text-sm">Status das Credenciais</span>
          
          <div className="grid grid-cols-2 gap-3 text-sm">
            {/* Instagram Business Account ID */}
            <div className="flex items-center justify-between p-2 rounded-md bg-muted/30">
              <span className="text-muted-foreground">Instagram ID</span>
              <StatusIndicator configured={!!credentials?.instagram_business_account_id} />
            </div>

            {/* Page ID (Facebook) */}
            <div className="flex items-center justify-between p-2 rounded-md bg-muted/30">
              <span className="text-muted-foreground">Page ID</span>
              <StatusIndicator configured={!!credentials?.page_id} />
            </div>

            {/* Access Token */}
            <div className="flex items-center justify-between p-2 rounded-md bg-muted/30">
              <span className="text-muted-foreground">Access Token</span>
              <StatusIndicator configured={!!credentials?.has_access_token} />
            </div>

            {/* App Secret */}
            <div className="flex items-center justify-between p-2 rounded-md bg-muted/30">
              <span className="text-muted-foreground">App Secret</span>
              <StatusIndicator configured={!!credentials?.has_app_secret} />
            </div>
          </div>
        </div>

        {/* Botão para verificar conexão */}
        {onCheckConnection && (
          <>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="text-sm">
                {connectionStatus === 'connected' && (
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    Conexão ativa
                  </span>
                )}
                {connectionStatus === 'disconnected' && (
                  <span className="flex items-center gap-1 text-amber-600">
                    <XCircle className="h-4 w-4" />
                    Desconectado
                  </span>
                )}
                {connectionStatus === 'error' && (
                  <span className="flex items-center gap-1 text-destructive">
                    <XCircle className="h-4 w-4" />
                    Erro na conexão
                  </span>
                )}
                {connectionStatus === 'pending' && (
                  <span className="text-muted-foreground">
                    Aguardando configuração do webhook
                  </span>
                )}
              </div>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={onCheckConnection}
                disabled={isCheckingConnection || !hasFullCredentials}
              >
                {isCheckingConnection ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Verificar Conexão
              </Button>
            </div>
          </>
        )}

        {/* Link para documentação */}
        <div className="pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open('https://developers.facebook.com/docs/instagram-api/overview', '_blank')}
            className="w-full gap-2 text-muted-foreground hover:text-foreground"
          >
            <ExternalLink className="h-4 w-4" />
            Ver Documentação Oficial da Meta
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default InstagramCredentialsCard;
